import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  withStyles
} from '@material-ui/core';
// Removed notistack dependency - enqueueSnackbar passed as prop
import { createColorMap, lightenColor, getStackColor } from './QuickNotesColorPalette';
import { cleanAggregatedChartDataWithColors } from './CategoryUtils';

// GraphQL query for YTD aggregates with date range support
const GET_YTD_AGGREGATES = gql`
  query GetQuickNotesYtdAggregates($stack: QuickNotesStackEnum!, $startDate: String, $endDate: String) {
    getQuickNotesYtdAggregates(stack: $stack, startDate: $startDate, endDate: $endDate) {
      month
      buckets {
        key
        count
        timeSpentMinutes
      }
      totals {
        count
        timeSpentMinutes
      }
    }
  }
`;

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
  },
  chartContainer: {
    height: 400,
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  emptyState: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
  },
  legendColor: {
    width: 12,
    height: 12,
    marginRight: theme.spacing(0.5),
    borderRadius: 2,
  },
});

const QuickNotesYtdChart = ({ 
  metric = 'count',
  stack = 'ACTIVITY_TYPE', 
  onDrilldown,
  onError,
  refreshTrigger = 0,
  classes,
  enqueueSnackbar,
  dateRange,
  selectedPeriod
}) => {
  
  // Convert container's metric format to component format
  const selectedMetric = metric;
  // Use stack as-is since container already provides correct GraphQL enum format
  const selectedStack = stack;

  const { data, loading, error, refetch } = useQuery(GET_YTD_AGGREGATES, {
    variables: {
      stack: selectedStack,
      startDate: dateRange?.startDate || null,
      endDate: dateRange?.endDate || null
    },
    errorPolicy: 'all',
    onError: (error) => {
      console.error('QuickNotesYtdChart: GraphQL error', error);
      enqueueSnackbar(`Error loading data: ${error.message}`, { variant: 'error' });
      
      // Propagate error to container if handler provided
      if (onError) {
        onError(error, 'loading chart data');
      }
    }
  });

  // Refetch data when props change
  useEffect(() => {
    if (refetch) {
      refetch({
        stack: selectedStack
      });
    }
  }, [selectedStack, refreshTrigger, refetch]);

  // Transform data for Recharts
  const transformDataForChart = (rawData) => {
    if (!rawData || !rawData.getQuickNotesYtdAggregates) return [];

    // Clean category names and get color map based on original names
    const { cleanedAggregates: aggregates, colorMap, originalKeyMap } = cleanAggregatedChartDataWithColors(
      rawData.getQuickNotesYtdAggregates, 
      selectedStack, 
      getStackColor
    );

    const transformedData = aggregates.map(monthData => {
      const chartDataPoint = {
        month: monthData.month,
        monthLabel: formatMonthLabel(monthData.month),
        total: selectedMetric === 'count' ? monthData.totals.count : monthData.totals.timeSpentMinutes,
      };

      // Add each bucket as a separate data point for stacking
      monthData.buckets.forEach(bucket => {
        const value = selectedMetric === 'count' ? bucket.count : bucket.timeSpentMinutes;
        chartDataPoint[bucket.key] = value;
      });

      return chartDataPoint;
    });

    // Sort by month chronologically (earliest to latest)
    const sortedData = transformedData.sort((a, b) => {
      return new Date(a.month + '-01') - new Date(b.month + '-01');
    });
    return { transformedData: sortedData, colorMap, originalKeyMap };
  };

  // Format month label for display
  const formatMonthLabel = (monthString) => {
    // Parse the month string manually to avoid timezone issues
    const [year, month] = monthString.split('-').map(Number);
    const date = new Date(year, month - 1, 1); // month - 1 because Date months are 0-indexed
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Get all unique stack keys for creating Bar components
  const getStackKeys = (transformedData) => {
    const keys = new Set();
    transformedData.forEach(dataPoint => {
      Object.keys(dataPoint).forEach(key => {
        if (key !== 'month' && key !== 'monthLabel' && key !== 'total') {
          keys.add(key);
        }
      });
    });
    return Array.from(keys);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      const unit = selectedMetric === 'count' ? 'notes' : 'minutes';

      return (
        <div 
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
          role="tooltip"
          aria-live="polite"
        >
          <Typography variant="subtitle2" style={{ marginBottom: '4px' }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" style={{ color: entry.color }}>
              <span aria-label={`${entry.dataKey} data`}>
                {entry.dataKey}: {entry.value} {unit}
              </span>
            </Typography>
          ))}
          <Typography variant="body2" style={{ fontWeight: 600, marginTop: '4px' }}>
            <span aria-label={`Total for ${label}`}>
              Total: {total} {unit}
            </span>
          </Typography>
        </div>
      );
    }
    return null;
  };

  // Handle bar click for drill-down
  const handleBarClick = (data, event) => {
    
    if (onDrilldown && data) {
      // For Recharts Bar charts, the clicked data structure is different
      // data contains the payload for the specific month that was clicked
      let monthData, displayStackKey;
      
      if (data.payload) {
        // Direct click on bar segment
        monthData = data.payload;
        displayStackKey = data.dataKey || data.name;
      } else if (data.activePayload && data.activePayload.length > 0) {
        // Click through activePayload (fallback)
        monthData = data.activePayload[0].payload;
        displayStackKey = data.activePayload[0].dataKey;
      } else {
        console.warn('YTD Chart: Unable to determine clicked segment data');
        return;
      }
      
      // For category and activity type drill-downs, use the original name from the database
      // For other stack dimensions, use the display key as-is
      let stackKey;
      if ((stack === 'CATEGORY' || stack === 'ACTIVITY_TYPE') && originalKeyMap[displayStackKey]) {
        stackKey = originalKeyMap[displayStackKey]; // Use original name from database
      } else {
        stackKey = displayStackKey; // Use display key for non-cleaned dimensions
      }
      
      // For YTD chart, we need to calculate the correct date range for the month
      const month = monthData.month; // Should be in format "2025-08"
      
      // Parse the month string to get year and month
      const [year, monthNum] = month.split('-').map(Number);
      
      // Create start date (first day of month)
      const startDate = `${year}-${monthNum.toString().padStart(2, '0')}-01`;
      
      // Create end date (last day of month)
      const lastDay = new Date(year, monthNum, 0).getDate(); // monthNum is 1-based, Date constructor expects 0-based
      const endDate = `${year}-${monthNum.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
      
      const drilldownData = {
        chartType: 'ytd',
        stackDimension: stack, // Use the container's stack format (renamed from 'stack' to 'stackDimension')
        stackKey: stackKey, // Now uses original category name for CATEGORY stack
        metric: selectedMetric,
        month: null, // YTD doesn't use month filter
        startDate: startDate,
        endDate: endDate,
        title: `${displayStackKey} - ${monthData.monthLabel || month}` // Keep display name for title
      };
      onDrilldown(drilldownData);
    }
  };

  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card className={classes.card}>
        <CardContent>
          <Typography color="error" align="center">
            Error loading YTD chart data. Please try again.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const chartResult = transformDataForChart(data);
  const transformedData = chartResult.transformedData || [];
  const colorMap = chartResult.colorMap || {};
  const originalKeyMap = chartResult.originalKeyMap || {};
  const stackKeys = getStackKeys(transformedData);

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.cardHeader}>
          <Typography variant="h6" component="h2" id="ytd-chart-title">
            Quick Notes Activity by Type
          </Typography>
          <Typography variant="body2" color="textSecondary" id="ytd-chart-description">
            {selectedMetric === 'count' ? 'Number of notes' : 'Total time spent (minutes)'} by month
          </Typography>
        </Box>



        {transformedData.length === 0 ? (
          <div className={classes.emptyState} role="status" aria-live="polite">
            <Typography variant="body1" component="p">
              No Quick Notes data available for the current year.
            </Typography>
            <Typography variant="body2" style={{ marginTop: '8px' }} color="textSecondary">
              Quick Notes will appear here once team members start logging their activities.
            </Typography>
          </div>
        ) : (
          <>
            <div className={classes.chartContainer} role="img" aria-labelledby="ytd-chart-title" aria-describedby="ytd-chart-description">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={transformedData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                  accessibilityLayer
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthLabel" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {stackKeys.map((key) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="stack"
                      fill={colorMap[key]}
                      name={key}
                      cursor="pointer"
                      onClick={(data, index, event) => {
                        handleBarClick({ ...data, dataKey: key, name: key }, event);
                      }}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

QuickNotesYtdChart.propTypes = {
  metric: PropTypes.oneOf(['count', 'time_spent']),
  stack: PropTypes.oneOf(['ACTIVITY_TYPE', 'PLANNED', 'CATEGORY']),
  onDrilldown: PropTypes.func,
  onError: PropTypes.func,
  refreshTrigger: PropTypes.number,
  classes: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  selectedPeriod: PropTypes.string,
};

export default withStyles(styles)(QuickNotesYtdChart);